import { createBookingInsightsApi } from "~/services/public/BookingInsightsApi"

export const state = (): BookingInsightsState => ({
  bookingsCopy: null,
  bookingsMap: null,
  bookingsReviews: null,
  currentBookingQuote: null,
  quoteId: null,
  currentBookingQuoteLoading: false,
  error: null,
  postalCode: null
})

export const GET_BOOKINGS_COPY = "getBookingsCopy"
export const GET_BOOKINGS_MAP = "getBookingsMap"
export const GET_BOOKINGS_REVIEWS = "getBookingsReviews"
export const GET_BOOKING_QUOTE = "getBookingQuote"
export const GET_POSTAL_CODE = "getPostalCode"
export const GET_BOOKING_QUOTE_LOADING = "getCurrentBookingQuoteLoading"
export const GET_ERROR = "getError"

export const getters = {
  [GET_BOOKINGS_COPY]: (state: BookingInsightsState) => state.bookingsCopy,
  [GET_BOOKINGS_MAP]: (state: BookingInsightsState) => state.bookingsMap,
  [GET_BOOKINGS_REVIEWS]: (state: BookingInsightsState) => state.bookingsReviews,
  [GET_BOOKING_QUOTE]: (state: BookingInsightsState) => state.currentBookingQuote,
  [GET_POSTAL_CODE]: (state: BookingInsightsState) => state.postalCode,
  [GET_BOOKING_QUOTE_LOADING]: (state: BookingInsightsState) => state.currentBookingQuoteLoading,
  [GET_ERROR]: (state: BookingInsightsState) => state.error
}

export const _SET_BOOKINGS_COUNT = "SET_BOOKINGS_COUNT"
export const _SET_BOOKINGS_MAP = "SET_BOOKINGS_MAP"
export const _SET_BOOKINGS_REVIEWS = "SET_BOOKINGS_REVIEWS"
export const _SET_BOOKING_QUOTE = "SET_BOOKING_QUOTE"
export const _SET_POSTAL_CODE = "SET_POSTAL_CODE"
export const _SET_QUOTE_ID = "SET_QUOTE_ID"
export const _SET_BOOKING_QUOTE_LOADING = "SET_BOOKING_QUOTE_LOADING"
export const _SET_ERROR = "SET_ERROR"

export const mutations = {
  [_SET_BOOKINGS_COUNT](state: BookingInsightsState, data: AvBookingsCopy) {
    state.bookingsCopy = data
  },
  [_SET_BOOKINGS_MAP](state: BookingInsightsState, data: AvMap) {
    state.bookingsMap = data
  },
  [_SET_BOOKINGS_REVIEWS](state: BookingInsightsState, data: AvReview[]) {
    state.bookingsReviews = data
  },
  [_SET_BOOKING_QUOTE](state: BookingInsightsState, data: BookingQuote) {
    state.currentBookingQuote = data
  },
  [_SET_POSTAL_CODE](state: BookingInsightsState, data: string) {
    state.postalCode = data
  },
  [_SET_QUOTE_ID](state: BookingInsightsState, data: string) {
    state.quoteId = data
  },
  [_SET_BOOKING_QUOTE_LOADING](state: BookingInsightsState, value: boolean) {
    state.currentBookingQuoteLoading = value
  },
  [_SET_ERROR](state: BookingInsightsState, error: string | null) {
    state.error = error
  }
}

export const FETCH_BOOKINGS_COUNT = "fetchBookingsCount"
export const FETCH_BOOKINGS_MAP = "fetchBookingsMap"
export const FETCH_BOOKINGS_REVIEWS = "fetchBookingsReviews"
export const FETCH_BOOKING_QUOTE = "fetchBookingQuote"
export const SET_POSTAL_CODE = "setPostalCode"
export const SET_QUOTE_ID = "setQuoteId"

export const actions = {
  async [FETCH_BOOKINGS_COUNT]({ commit }, { $axios, $config, params, originalPostalCode }) {
    commit(_SET_ERROR, null)

    try {
      const api = createBookingInsightsApi($axios, $config)
      const response = await api.getBookingsCount(params)
      let shortPostcode = response.data.meta.shortPostalCode
      if (shortPostcode.length === 4 &&
        !/^0/.test(shortPostcode) &&
        originalPostalCode.length === 5 &&
        /^0/.test(originalPostalCode)) {
        shortPostcode = originalPostalCode
      }
      const transformedData = {
        num_jobs_year: response.data.total_bookings, // referenced from WordPress Local page
        total_bookings: response.data.total_bookings,
        location: response.data.location,
        timePeriod: response.data.timePeriod,
        cheeky_title: response.data.meta.associatedTitle,
        short_postcode: shortPostcode,
        average_four_bed_home_move_cost: response.data.meta.averageFourBedHomeMoveCost,
        average_home_move_cost: response.data.meta.averageHomeMoveCost,
        average_number_of_moves_by_agent: response.data.meta.averageNumberOfMovesByAgent,
        average_savings: response.data.meta.averageSavings,
        faq_average_cost: response.data.meta.faqAverageCost,
        faq_move_near_me: response.data.meta.faqMoveNearMe,
        currency: response.data.meta.currency,
        number_of_quotes_in_area: response.data.meta.numberOfQuotesInArea
      }
      commit(_SET_BOOKINGS_COUNT, transformedData)
      return true
    } catch (error: unknown) {
      const err = error as Error
      commit(_SET_ERROR, err.message)
      return err
    }
  },

  async [FETCH_BOOKINGS_MAP]({ commit }, { $axios, $config, params }) {
    commit(_SET_ERROR, null)

    try {
      const api = createBookingInsightsApi($axios, $config)
      const response = await api.getBookingsMap(params)
      const transformedData: AvPinData[] = response.data.pins.map(pin => ({
        lat: pin.latitude,
        lng: pin.longitude,
        id: pin.id,
        imageID: pin.meta.imageID,
        stars: pin.meta.stars,
        textLine1: pin.meta.bookingDescription,
        textLine2: pin.meta.distance,
        textLine3: pin.meta.price
      }))
      commit(_SET_BOOKINGS_MAP, { pins: transformedData })
      return true
    } catch (error: unknown) {
      const err = error as Error
      commit(_SET_ERROR, err.message)
    }
  },

  async [FETCH_BOOKINGS_REVIEWS]({ commit }, { $axios, $config, params }) {
    commit(_SET_ERROR, null)

    try {
      const api = createBookingInsightsApi($axios, $config)
      const response = await api.getBookingsReviews(params)
      const transformedData: InternalReview[] = response.data.reviews.map(review => ({
        id: review.id,
        name: review.reviewerName,
        rating: review.rating,
        type: review.reviewDescription || "home",
        location: review.address,
        shortPostalCode: review.meta.shortPostalCode,
        typeEmoji: "🏡",
        review: review.reviewComment
      }))
      commit(_SET_BOOKINGS_REVIEWS, transformedData)
      return true
    } catch (error: unknown) {
      const err = error as Error
      commit(_SET_ERROR, err.message)
    }
  },

  async [FETCH_BOOKING_QUOTE]({ commit }, { $axios, $config, params }) {
    commit(_SET_BOOKING_QUOTE_LOADING, true)
    commit(_SET_ERROR, null)

    try {
      const api = createBookingInsightsApi($axios, $config)
      const response = await api.getBookingQuote(params)

      const quoteData = <BookingQuote>{
        quoteId: response.data.quoteId,
        quotePrice: response.data.quotePrice,
        quoteDescription: response.data.quoteDescription,
        quoteCurrency: response.data.quoteCurrency,
        quoteStatus: response.data.quoteStatus,
        scheduledDate: response.data.scheduledDate,
        continueQuoteUrl: response.data.continueQuoteUrl || "",
        locations: response.data.locations || {},
        items: response.data.items || [],
        agentInteraction: { ...response.data.agentInteraction }
      }
      commit(_SET_BOOKING_QUOTE, quoteData)
      return true
    } catch (error: unknown) {
      const err = error as Error
      commit(_SET_ERROR, err.message)
      return err
    } finally {
      commit(_SET_BOOKING_QUOTE_LOADING, false)
    }
  },

  async [SET_POSTAL_CODE]({ commit }, { params }) {
    commit(_SET_POSTAL_CODE, params)
  },

  async [SET_QUOTE_ID]({ commit }, { params }) {
    commit(_SET_QUOTE_ID, params)
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
